<template>
  <section class="ic-home-reno">
    <div class="ic-home-reno__head">
      <span class="ic-home-pill">
        <Hammer class="ic-home-pill__icon" :size="19" :stroke-width="2.5" />
        Realizzala
      </span>
      <h2 class="ic-home-reno__title ic-home-sectitle">E se la volessi proprio così?</h2>
      <p class="ic-home-reno__lead">
        L'abbiamo pensata e possiamo realizzarla.<br />
        Ti seguiamo dall'acquisto alla consegna chiavi in mano.
      </p>
    </div>

    <!-- Horizontal scroller of process steps. Grey placeholders for now; the
         real photos will be dropped in later. Navigate with the arrows,
         trackpad, or drag. -->
    <div class="ic-home-reno__scroller" ref="scroller">
      <figure v-for="(step, i) in steps" :key="i" class="ic-home-reno__card">
        <div class="ic-home-reno__thumb">
          <img
            v-if="step.image"
            :src="step.image"
            :alt="step.label"
            class="ic-home-reno__img"
            loading="lazy"
          />
        </div>
        <figcaption class="ic-home-reno__caption">{{ i + 1 }}. {{ step.label }}</figcaption>
      </figure>
    </div>

    <div class="ic-home-reno__footer">
      <button type="button" data-testid="home-start" class="ic-home-btn ic-home-reno__btn" @click="showChoice = true">
        Inizia
      </button>
      <div class="ic-home-reno__nav">
        <button type="button" class="ic-home-reno__arrow" aria-label="Step precedenti" @click="scrollByStep(-1)">
          <ArrowLeft :size="22" />
        </button>
        <button type="button" class="ic-home-reno__arrow" aria-label="Step successivi" @click="scrollByStep(1)">
          <ArrowRight :size="22" />
        </button>
      </div>
    </div>

    <!-- On "Inizia", let the user pick how to start: a free videocall or a
         quote request (the dedicated wizard page). -->
    <base-popup
      :show="showChoice"
      card-class="ic-home-choice"
      close-test-id="home-choice-close"
      @close="showChoice = false"
    >
      <h3 class="ic-home-choice__title">Come vuoi iniziare?</h3>
      <p class="ic-home-choice__sub">Scegli come dare il via alla tua ristrutturazione.</p>
      <div class="ic-home-choice__options">
        <button type="button" data-testid="home-vc-open" class="ic-home-choice__card" @click="chooseVideocall">
          <span class="ic-home-choice__icon"><Video :size="24" /></span>
          <span class="ic-home-choice__body">
            <span class="ic-home-choice__name">Prenota una videochiamata gratuita</span>
            <span class="ic-home-choice__desc">Parla con un progettista certificato e progetta la tua ristrutturazione.</span>
          </span>
          <ChevronRight :size="22" class="ic-home-choice__arrow" />
        </button>
        <button type="button" data-testid="home-quote" class="ic-home-choice__card" @click="choosePreventivo">
          <span class="ic-home-choice__icon"><FileText :size="24" /></span>
          <span class="ic-home-choice__body">
            <span class="ic-home-choice__name">Richiedi un preventivo</span>
            <span class="ic-home-choice__desc">Descrivi il tuo progetto e ricevi una stima su misura.</span>
          </span>
          <ChevronRight :size="22" class="ic-home-choice__arrow" />
        </button>
      </div>
    </base-popup>

    <videocall-flow
      ref="videocallFlow"
      source="immaginacasa-home"
      intro-body-text="Richiedi una videochiamata di 30 minuti con un progettista certificato, per discutere di un progetto di ristrutturazione."
    />
  </section>
</template>

<script>
import { ArrowLeft, ArrowRight, Video, FileText, ChevronRight, Hammer } from 'lucide-vue-next';
import consulenza from '@/assets/img/renovation-1.jpg';
import sopralluogo from '@/assets/img/renovation-2.jpg';
import lavori from '@/assets/img/renovation-3.jpg';
import BasePopup from '@/components/BasePopup.vue';
import VideocallFlow from '@/components/Videocall/VideocallFlow.vue';
import { renovationCta } from '@/renovationCta.js';
import { trackEvent } from '@/services/analytics';

export default {
  name: 'HomeRenovationCall',
  components: { ArrowLeft, ArrowRight, Video, FileText, ChevronRight, Hammer, BasePopup, VideocallFlow },
  computed: {
    // The header's "Ristruttura" pill bumps this counter to ask us to open the
    // choice popup (it can't reach `showChoice` directly — see renovationCta.js).
    renovationOpenRequests() {
      return renovationCta.openRequests;
    },
  },
  watch: {
    renovationOpenRequests() {
      this.showChoice = true;
    },
  },
  data() {
    return {
      // The renovation journey, step by step. Photos are dropped in as they
      // arrive; steps without one keep the grey placeholder for now.
      steps: [
        { label: 'Consulenza e progettazione', image: consulenza },
        { label: 'Sopralluogo e preventivo', image: sopralluogo },
        { label: 'Lavori di ristrutturazione', image: lavori },
      ],
      showChoice: false,
    };
  },
  methods: {
    scrollByStep(dir) {
      const scroller = this.$refs.scroller;
      if (!scroller) return;
      const card = scroller.querySelector('.ic-home-reno__card');
      const gap = 24;
      const amount = card ? card.getBoundingClientRect().width + gap : scroller.clientWidth;
      scroller.scrollBy({ left: dir * amount, behavior: 'smooth' });
    },
    chooseVideocall() {
      this.showChoice = false;
      // GA4 (#501): videocall intent from the home renovation CTA. Same event
      // as the detail page; source distinguishes where it started.
      trackEvent('videocall_open', { source: 'home' });
      this.$refs.videocallFlow.open();
    },
    choosePreventivo() {
      this.showChoice = false;
      // GA4 (#501): user started the renovation-quote flow.
      trackEvent('renovation_quote_start', { source: 'home' });
      this.$router.push('/user/preventivi-ristrutturazione/add');
    },
  },
};
</script>
